/* ==========================================================================
   FORG3D — client-side money formatting (PURE. No DOM. No dependencies.)

   Runs in the browser (global FORG3DMoney) and in Node (module.exports) so the
   derivation is unit-tested independently of any UI.

   WHY THIS EXISTS
   ---------------
   Every JS money formatter in this theme used to look like:

       if (Shopify.formatMoney) { ...Shopify.currency.format... }
       return '$' + (cents / 100).toFixed(2);          // <- hardcoded USD

   On the live store `Shopify.formatMoney`, `Shopify.money_format` AND
   `Shopify.currency.format` are all undefined, so that fallback was the only
   reachable path. Every non-USD market rendered EUR/GBP/CAD amounts with a
   dollar sign and US punctuation — a EUR cart of 149,85 showed as "$149.85".
   Reported by a customer 2026-08-23 (Chatwoot conv. 214).

   THE APPROACH
   ------------
   Rather than guess the format or hand-roll a currency table, layout/theme.liquid
   renders one known amount through Liquid's OWN `money` filter and hands us the
   result. We derive the active presentment currency's punctuation from that
   sample. This guarantees JS output matches what Liquid renders elsewhere on the
   page — the exact invariant whose absence caused the bug.

     $1,234.56  ->  prefix "$"  group ","  decimal "."
     €1.234,56  ->  prefix "€"  group "."  decimal ","
     1 234,56 € ->  suffix " €" group " "  decimal ","
     ¥123,456   ->  prefix "¥"  group ","  decimal ""   (zero-decimal currency)
   ========================================================================== */
(function (root, factory) {
  var api = factory();
  if (root) { root.FORG3DMoney = api; }
  if (typeof module !== 'undefined' && module.exports) { module.exports = api; }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';

  /* The amount theme.liquid renders through `money` to produce the sample.
     1234.56 is deliberate: it exercises BOTH the grouping separator and the
     decimal separator in a single sample. */
  var SAMPLE_CENTS = 123456;

  var USD_FALLBACK = {
    prefix: '$', suffix: '', groupSep: ',', decimalSep: '.', decimals: 2
  };

  /**
   * Derive a format descriptor from Liquid's `money` output for SAMPLE_CENTS.
   * Returns the USD fallback for unusable input rather than throwing — a bad
   * sample must never take the cart down.
   */
  function deriveMoneyFormat(sample) {
    var s = (sample == null ? '' : String(sample)).trim();
    if (!s) { return USD_FALLBACK; }

    var first = -1, last = -1, i;
    for (i = 0; i < s.length; i++) { if (s.charAt(i) >= '0' && s.charAt(i) <= '9') { first = i; break; } }
    for (i = s.length - 1; i >= 0; i--) { if (s.charAt(i) >= '0' && s.charAt(i) <= '9') { last = i; break; } }
    if (first === -1) { return USD_FALLBACK; }

    var body = s.slice(first, last + 1);
    var seps = body.replace(/[0-9]/g, '');

    var decimalSep = '';
    var groupSep = '';

    if (seps.length) {
      var lastSep = seps.charAt(seps.length - 1);
      var lastIdx = body.lastIndexOf(lastSep);
      /* Everything after the final separator is digits by construction. A
         trailing run of exactly 2 means it is the decimal separator; grouping
         runs are always 3 (true even for Indian lakh grouping: 1,23,456). */
      var trailing = body.length - lastIdx - 1;
      if (trailing === 2) {
        decimalSep = lastSep;
        var head = body.slice(0, lastIdx).replace(/[0-9]/g, '');
        groupSep = head.length ? head.charAt(0) : '';
      } else {
        groupSep = lastSep;
      }
    }

    return {
      prefix: s.slice(0, first),
      suffix: s.slice(last + 1),
      groupSep: groupSep,
      decimalSep: decimalSep,
      decimals: decimalSep ? 2 : 0
    };
  }

  function group(intStr, sep) {
    if (!sep) { return intStr; }
    var out = '';
    var count = 0;
    for (var i = intStr.length - 1; i >= 0; i--) {
      out = intStr.charAt(i) + out;
      count++;
      if (count % 3 === 0 && i > 0) { out = sep + out; }
    }
    return out;
  }

  /**
   * Format a Shopify minor-unit amount (what /cart.js returns) using a
   * descriptor from deriveMoneyFormat.
   */
  function formatMoney(cents, fmt) {
    fmt = fmt || USD_FALLBACK;
    var n = Number(cents);
    if (!isFinite(n)) { n = 0; }

    var negative = n < 0;
    n = Math.abs(n);

    var value = n / 100;
    var fixed = fmt.decimals ? value.toFixed(2) : String(Math.round(value));
    var parts = fixed.split('.');
    var intPart = group(parts[0], fmt.groupSep);
    var decPart = parts[1] || '';

    var out = fmt.prefix + intPart
      + (fmt.decimals ? fmt.decimalSep + decPart : '')
      + fmt.suffix;

    return (negative ? '-' : '') + out;
  }

  /**
   * Match Liquid's `money_without_trailing_zeros`: drop the decimal pair when
   * the amount is a whole unit. The cart drawer's subtotal is rendered with
   * that filter on first paint, so its JS re-render must agree or the total
   * visibly changes shape ($168 -> $224.00) on the first quantity click.
   */
  function formatMoneyCompact(cents, fmt) {
    fmt = fmt || USD_FALLBACK;
    var n = Number(cents);
    if (!isFinite(n)) { n = 0; }
    if (fmt.decimals && Math.abs(n) % 100 === 0) {
      return formatMoney(cents, {
        prefix: fmt.prefix,
        suffix: fmt.suffix,
        groupSep: fmt.groupSep,
        decimalSep: fmt.decimalSep,
        decimals: 0
      });
    }
    return formatMoney(cents, fmt);
  }

  /* ---------------------------------------------------------------- browser */

  var cached = null;

  /** The format for the CURRENT request's presentment currency. */
  function activeFormat() {
    if (cached) { return cached; }
    var g = (typeof window !== 'undefined') ? window : null;
    var sample = g && g.FORG3D && g.FORG3D.moneySample;
    cached = sample ? deriveMoneyFormat(sample) : USD_FALLBACK;
    return cached;
  }

  /** Convenience wrapper — the call every UI module should use. */
  function format(cents) {
    return formatMoney(cents, activeFormat());
  }

  /** As `format`, but matching Liquid's money_without_trailing_zeros. */
  function compact(cents) {
    return formatMoneyCompact(cents, activeFormat());
  }

  /** ISO code for the presentment currency, for "€49,95 EUR" style suffixes. */
  function isoCode() {
    var g = (typeof window !== 'undefined') ? window : null;
    return (g && g.FORG3D && g.FORG3D.moneyIso)
      || (g && g.Shopify && g.Shopify.currency && g.Shopify.currency.active)
      || 'USD';
  }

  return {
    SAMPLE_CENTS: SAMPLE_CENTS,
    USD_FALLBACK: USD_FALLBACK,
    deriveMoneyFormat: deriveMoneyFormat,
    formatMoney: formatMoney,
    formatMoneyCompact: formatMoneyCompact,
    activeFormat: activeFormat,
    format: format,
    compact: compact,
    isoCode: isoCode
  };
});
